/* eslint-disable camelcase */

type ShopPayTerm = {
  apr: number;
  loan_type: string;
  installments_count: number;
};

type ShopPayFinancingPlan = {
  min_price: string;
  max_price: string;
  terms: ShopPayTerm[];
};

// Extract these fields from the 'shopify-payment-terms' element on the PDP.
export type ShopPayTerms = {
  min_price: string;
  max_price: string;
  financing_plans: ShopPayFinancingPlan[];
  installments_buyer_prequalification_enabled: boolean;
  seller_id: number;
};

type ShopPayMetaVariant = {
  id: number;
  price_per_term: string;
  full_price: string;
  eligible: boolean;
  available: boolean;
  number_of_payment_terms: number;
};

type ShopPayMeta = {
  type: 'product';
  currency_code: 'USD';
  country_code: 'US';
  variants: ShopPayMetaVariant[];
  min_price: string;
  max_price: string;
  financing_plans: ShopPayFinancingPlan[];
  installments_buyer_prequalification_enabled: boolean;
  seller_id: number;
};

const usdPriceFormatter = new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' });

function coerceShopPayId(value: number | string | undefined): number | undefined {
  if (typeof value === 'number') {
    return Number.isFinite(value) ? value : undefined;
  }
  if (typeof value === 'string' && value !== '') {
    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : undefined;
  }
  return undefined;
}

function toShopPayPriceNumber(price: string): number {
  return Number(price.replace(/[$,]/g, ''));
}

function formatUsd(price: number): string {
  return usdPriceFormatter.format(price);
}

function floorToCents(price: number): number {
  return Math.floor(price * 100) / 100;
}

function getEligibleShopPayPlans(price: number, shopPayTerms: ShopPayTerms): ShopPayFinancingPlan[] {
  return shopPayTerms.financing_plans.filter(
    (plan) => price >= toShopPayPriceNumber(plan.min_price) && price <= toShopPayPriceNumber(plan.max_price),
  );
}

function getShopPaySplitTerm(price: number, shopPayTerms: ShopPayTerms): ShopPayTerm | undefined {
  return getEligibleShopPayPlans(price, shopPayTerms)
    .find((plan) => plan.terms.some((term) => term.loan_type === 'split_pay'))
    ?.terms.find((term) => term.loan_type === 'split_pay');
}

export function getShopPayText(
  price: number,
  variantId: number | string | undefined,
  available: boolean,
  shopPayTerms: ShopPayTerms | undefined,
  country: string | undefined,
  currency: string | undefined,
): string | undefined {
  const coercedVariantId = coerceShopPayId(variantId);

  if (
    !shopPayTerms ||
    country !== 'US' ||
    currency !== 'USD' ||
    !coercedVariantId ||
    !Number.isFinite(price)
  ) {
    return undefined;
  }

  const minPrice = toShopPayPriceNumber(shopPayTerms.min_price);
  const maxPrice = toShopPayPriceNumber(shopPayTerms.max_price);
  const eligible = price >= minPrice && price <= maxPrice;
  const splitPayTerm = getShopPaySplitTerm(price, shopPayTerms);
  const numberOfPaymentTerms = splitPayTerm?.installments_count ?? 4;

  const meta: ShopPayMeta = {
    type: 'product',
    currency_code: 'USD',
    country_code: 'US',
    variants: [
      {
        id: coercedVariantId,
        price_per_term: formatUsd(floorToCents(price / numberOfPaymentTerms)),
        full_price: formatUsd(price),
        eligible,
        available,
        number_of_payment_terms: numberOfPaymentTerms,
      },
    ],
    min_price: shopPayTerms.min_price,
    max_price: shopPayTerms.max_price,
    financing_plans: shopPayTerms.financing_plans,
    installments_buyer_prequalification_enabled: shopPayTerms.installments_buyer_prequalification_enabled,
    seller_id: shopPayTerms.seller_id,
  };

  return JSON.stringify(meta);
}
